/* eslint-disable no-console */
import NotificationSubscriptions from '../api/notificationSubscription';
import auth from '../api/auth';
import { useAlert } from 'dashboard/composables';

const BROWSER_NOTIFICATIONS_DISABLED_KEY = 'cw_browser_notifications_disabled';

const isPushFeatureLocallyDisabled = () => {
  try {
    return (
      window.localStorage.getItem(BROWSER_NOTIFICATIONS_DISABLED_KEY) === 'true'
    );
  } catch (_) {
    return false;
  }
};

const setPushFeatureLocallyDisabled = disabled => {
  try {
    if (disabled) {
      window.localStorage.setItem(BROWSER_NOTIFICATIONS_DISABLED_KEY, 'true');
    } else {
      window.localStorage.removeItem(BROWSER_NOTIFICATIONS_DISABLED_KEY);
    }
  } catch (_) {
    // Ignore storage failures and fall back to permission state only.
  }
};

export const supportsWebPush = () => {
  return 'serviceWorker' in navigator && 'PushManager' in window;
};

export const verifyServiceWorkerExistence = (callback = () => {}) => {
  if (!('serviceWorker' in navigator)) {
    // Service Worker isn't supported on this browser, disable or hide UI.
    return Promise.resolve(null);
  }

  return navigator.serviceWorker
    .register('/sw.js')
    .then(registration => {
      callback(registration);
      return registration;
    })
    .catch(registrationError => {
      // eslint-disable-next-line
      console.log('SW registration failed: ', registrationError);
      return null;
    });
};

export const hasPushPermissions = () => {
  if ('Notification' in window) {
    return Notification.permission === 'granted';
  }
  return false;
};

export const hasEnabledPushNotifications = () => {
  return hasPushPermissions() && !isPushFeatureLocallyDisabled();
};

const generateKeys = str =>
  btoa(String.fromCharCode.apply(null, new Uint8Array(str)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_');

export const getPushSubscriptionPayload = subscription => ({
  notification_subscription: {
    subscription_type: 'browser_push',
    subscription_attributes: {
      endpoint: subscription.endpoint,
      p256dh: generateKeys(subscription.getKey('p256dh')),
      auth: generateKeys(subscription.getKey('auth')),
    },
  },
});

export const sendRegistrationToServer = subscription => {
  if (auth.hasAuthCookie()) {
    return NotificationSubscriptions.create(
      getPushSubscriptionPayload(subscription)
    );
  }
  return null;
};

export const registerSubscription = async (onSuccess = () => {}) => {
  try {
    setPushFeatureLocallyDisabled(false);

    if (!window.chatwootConfig.vapidPublicKey || !supportsWebPush()) {
      onSuccess();
      return true;
    }

    await verifyServiceWorkerExistence();
    const serviceWorkerRegistration = await navigator.serviceWorker.ready;
    let subscription =
      await serviceWorkerRegistration.pushManager.getSubscription();

    if (!subscription) {
      subscription = await serviceWorkerRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: window.chatwootConfig.vapidPublicKey,
      });
    }

    await sendRegistrationToServer(subscription);
    onSuccess();
    return true;
  } catch (_) {
    useAlert('This browser does not support desktop notification');
    return false;
  }
};

const requestBrowserPermission = async () => {
  if (typeof Notification.requestPermission !== 'function') {
    return Notification.permission;
  }

  try {
    return await Notification.requestPermission();
  } catch (_) {
    return new Promise(resolve => {
      Notification.requestPermission(permission => resolve(permission));
    });
  }
};

export const requestPushPermissions = async ({ onSuccess }) => {
  if (!('Notification' in window)) {
    useAlert('This browser does not support desktop notification');
    return false;
  }

  if (Notification.permission === 'granted') {
    setPushFeatureLocallyDisabled(false);
    return registerSubscription(onSuccess);
  }

  if (Notification.permission === 'denied') {
    useAlert(
      'Browser notification permission is blocked. Please enable it in your browser settings'
    );
    return false;
  }

  const permission = await requestBrowserPermission();
  if (permission === 'granted') {
    setPushFeatureLocallyDisabled(false);
    const registration = await verifyServiceWorkerExistence();
    if (
      supportsWebPush() &&
      window.chatwootConfig.vapidPublicKey &&
      !registration
    ) {
      useAlert('Unable to register this browser for push notifications');
      return false;
    }
    return registerSubscription(onSuccess);
  }

  useAlert('Push notification permission was not granted');
  return false;
};

export const getExistingPushSubscription = async () => {
  if (!supportsWebPush()) {
    return null;
  }

  const registration = await verifyServiceWorkerExistence();
  if (!registration) {
    return null;
  }

  try {
    return await registration.pushManager.getSubscription();
  } catch (_) {
    return null;
  }
};

export const unsubscribeExistingPushSubscription = async () => {
  setPushFeatureLocallyDisabled(true);

  const subscription = await getExistingPushSubscription();
  if (!subscription) return false;

  try {
    await subscription.unsubscribe();
    return true;
  } catch (_) {
    return false;
  }
};
